import { useEffect, useRef, useState } from "react";

interface UseLoadTimeoutGuardOptions {
  hasLoaded: boolean;
  resetKey: string;
  timeoutMs?: number;
}

interface UseLoadTimeoutGuardResult {
  isLoading: boolean;
  hasTimedOut: boolean;
}

export const useLoadTimeoutGuard = ({
  hasLoaded,
  resetKey,
  timeoutMs = 10000,
}: UseLoadTimeoutGuardOptions): UseLoadTimeoutGuardResult => {
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    setHasTimedOut(false);
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [resetKey]);

  useEffect(() => {
    if (hasLoaded || hasTimedOut) {
      return;
    }

    timeoutRef.current = window.setTimeout(() => {
      setHasTimedOut(true);
    }, timeoutMs);

    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [hasLoaded, hasTimedOut, timeoutMs]);

  return {
    isLoading: !hasLoaded && !hasTimedOut,
    hasTimedOut,
  };
};
